/**
 * Tâches : ce que la liste, la création et la modification partagent sans
 * rien dessiner — forme lue de l'API, adresses et préremplissages validés,
 * créneau du planning, corps envoyé au serveur. Testé sans navigateur.
 */
import { approxClock, approxMarginLabel, type TaskPriority, type TimeApproxMinutes } from '@myequip/shared';
import { dateFr } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { deadlineText, newObjectivePath, objectiveState } from './objective.js';

export interface Task {
  id: string;
  title: string;
  details: string | null;
  priority: TaskPriority;
  due_on: string | null;
  status: 'a_faire' | 'faite';
  assignee_id: string;
  created_by: string;
  assignee_name: string;
  created_by_name: string;
  done_at: string | null;
  /** Créneau dans le planning, début et fin ensemble. */
  planned_start: string | null;
  planned_end: string | null;
  /** Heure approximative du créneau : marge en minutes autour du début. */
  time_approx_minutes?: number | null;
  /** Heure limite : la tâche est un objectif. */
  due_at: string | null;
  /** « C'est fini quand… » */
  done_criteria: string | null;
  blocked_at: string | null;
  blocked_reason: string | null;
  /** Photo de preuve d'un objectif (la dernière, même rouvert). */
  proof_file_id: string | null;
  /** Numéro de celui qui a donné la tâche, rendu à la seule personne qui l'a reçue. */
  created_by_phone: string | null;
}

export interface TaskCounts {
  openCount: number;
  givenOpenCount: number;
  canAssign: boolean;
}

/** Onglets de la liste (`onglet`) : les miennes, celles données à l'équipe, les faites. */
export type TaskTab = 'mes' | 'equipe' | 'faites';
export const TASK_TABS: readonly TaskTab[] = ['mes', 'equipe', 'faites'];

/** Portée et état demandés à l'API pour un onglet. */
export function tabQuery(tab: TaskTab): { scope: 'mine' | 'given' | 'all'; status: 'a_faire' | 'faite' } {
  if (tab === 'equipe') return { scope: 'given', status: 'a_faire' };
  if (tab === 'faites') return { scope: 'all', status: 'faite' };
  return { scope: 'mine', status: 'a_faire' };
}

/**
 * Anciennes adresses (avis, accueil, planning, signets) : `?vue=given|done`,
 * `?tache=<id>`, `?nouvelle=1&debut=&limite=`. Relues une fois, puis l'adresse
 * est réécrite sous les noms communs (`onglet`, `id`) ou mène à la création.
 */
export const LEGACY_TASK_PARAMS = { tache: 'tache', nouvelle: 'nouvelle', debut: 'debut', limite: 'limite' } as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Ce que l'ancienne adresse demandait, sous les noms communs ; `null` : rien à réécrire. */
export function legacyTaskTarget(params: URLSearchParams): string | null {
  const read = (name: string): string => params.get(name) ?? '';
  const legacy = Object.values(LEGACY_TASK_PARAMS).some((name) => params.has(name));
  const vue = read('vue');
  const oldView = vue === 'given' || vue === 'done' || vue === 'mine';
  if (!legacy && !oldView) return null;
  if (read(LEGACY_TASK_PARAMS.nouvelle) === '1') {
    const valid = (value: string): boolean => LOCAL_DATE_TIME.test(value) && calendarDay(value.slice(0, 10)) && HH_MM.test(value.slice(11));
    const limite = read(LEGACY_TASK_PARAMS.limite);
    const debut = read(LEGACY_TASK_PARAMS.debut);
    if (valid(limite)) return newObjectivePath(limite);
    return newTaskPath(valid(debut) ? debut : undefined);
  }
  const next = new URLSearchParams();
  const tab = vue === 'given' ? 'equipe' : vue === 'done' ? 'faites' : '';
  if (tab) next.set('onglet', tab);
  const id = read(LEGACY_TASK_PARAMS.tache);
  if (UUID.test(id)) next.set('id', id);
  const query = next.toString();
  return `/taches${query ? `?${query}` : ''}`;
}

/** Créer une tâche, posée à une heure du planning (« AAAA-MM-JJThh:mm », heure locale). */
export function newTaskPath(startsAt?: string): string {
  if (!startsAt) return '/taches/nouvelle';
  const [date, heure] = startsAt.split('T');
  return `/taches/nouvelle?date=${date}&heure=${encodeURIComponent((heure ?? '').slice(0, 5))}`;
}

/** Ce que l'écran d'origine sait déjà, validé : une adresse modifiée à la main ne remplit rien d'invalide. */
export interface TaskPreset {
  /** Début du créneau (« AAAA-MM-JJThh:mm ») ou heure limite d'un objectif. */
  at: string;
  objective: boolean;
  /** Personne choisie par l'écran d'origine ; vérifiée contre la liste permise. */
  assignee: string;
}

/** Vrai pour un jour qui existe au calendrier : un 31 février glisserait au mois suivant. */
function calendarDay(day: string): boolean {
  if (!ISO_DAY.test(day)) return false;
  const date = new Date(`${day}T12:00`);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` === day;
}

export function taskPresetFromSearch(params: URLSearchParams): TaskPreset {
  const date = params.get('date') ?? '';
  const heure = params.get('heure') ?? '';
  const at = calendarDay(date) && HH_MM.test(heure) ? `${date}T${heure}` : '';
  const technicien = params.get('technicien') ?? '';
  return { at, objective: Boolean(at) && params.get('type') === 'objectif', assignee: UUID.test(technicien) ? technicien : '' };
}

export function localDateTime(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Durées proposées pour un créneau du planning. */
export const SLOT_MINUTES = ['30', '60', '120', '240'] as const;
export type SlotMinutes = (typeof SLOT_MINUTES)[number];

/**
 * Créneau saisi → corps de la requête ; sans début, le créneau est retiré. La
 * marge d'une heure approximative voyage toujours avec son créneau.
 */
export function slotBody(start: string, minutes: number, approx: TimeApproxMinutes | null = null): {
  plannedStart: string | null; plannedEnd: string | null; timeApproxMinutes: TimeApproxMinutes | null;
} {
  if (!start) return { plannedStart: null, plannedEnd: null, timeApproxMinutes: null };
  const begins = new Date(start);
  return {
    plannedStart: begins.toISOString(),
    plannedEnd: new Date(begins.getTime() + minutes * 60_000).toISOString(),
    timeApproxMinutes: approx,
  };
}

/** Durée d'un créneau enregistré, ramenée à la durée proposée la plus proche. */
export function slotMinutesOf(task: Pick<Task, 'planned_start' | 'planned_end'>): SlotMinutes {
  if (!task.planned_start || !task.planned_end) return '60';
  const minutes = Math.round((Date.parse(task.planned_end) - Date.parse(task.planned_start)) / 60_000);
  return SLOT_MINUTES.reduce((best, value) =>
    Math.abs(Number(value) - minutes) < Math.abs(Number(best) - minutes) ? value : best, '60' as SlotMinutes);
}

/**
 * « jeu. 15 oct., 14:00 – 15:30 » : le créneau tel qu'on le lit ;
 * « jeu. 15 oct., ≈ 14:00 (± 1 h) » quand l'heure est approximative.
 */
export function slotLabel(start: string, end: string, approx?: number | null): string {
  const begins = new Date(start);
  const time = (date: Date): string => date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const day = begins.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  if (approx) return `${day}, ${approxClock(begins, approx)} (${approxMarginLabel(approx)})`;
  return `${day}, ${time(begins)} – ${time(new Date(end))}`;
}

export function isLate(task: Pick<Task, 'status' | 'due_on'>, today = new Date()): boolean {
  return task.status === 'a_faire' && task.due_on !== null && task.due_on < today.toLocaleDateString('sv-SE');
}

/** État d'un objectif (tâche avec heure limite), sinon rien. */
export function stateOf(task: Task): ReturnType<typeof objectiveState> | undefined {
  return task.due_at ? objectiveState({ ...task, due_at: task.due_at }) : undefined;
}

/** Quand : heure limite, date, créneau — la ligne de contexte d'une tâche. */
export function whenLine(task: Task): string | undefined {
  const parts: string[] = [];
  if (task.due_at) parts.push(t('taches.pour', { quand: deadlineText(task.due_at) }));
  else if (task.due_on) parts.push(isLate(task) ? t('taches.enRetard', { date: dateFr(task.due_on) }) : t('taches.pourLe', { date: dateFr(task.due_on) }));
  if (task.planned_start && task.planned_end) {
    parts.push(t('taches.auPlanning', { creneau: slotLabel(task.planned_start, task.planned_end, task.time_approx_minutes) }));
  }
  return parts.length ? parts.join(' · ') : undefined;
}

/** Qui : la personne qui l'a donnée, ou celle qui l'a reçue, vue par `meId`. */
export function whoLine(task: Task, meId: string | undefined): string {
  const mine = task.assignee_id === meId;
  const author = task.created_by === meId;
  if (mine && author) return task.due_at ? t('taches.objectifPersonnel') : t('taches.personnelle');
  if (mine) return t('taches.donneePar', { nom: task.created_by_name });
  return t('taches.pourQui', { nom: task.assignee_name });
}

/** Saisie d'une tâche, commune à la création et à la modification. */
export interface TaskForm {
  title: string;
  details: string;
  priority: TaskPriority;
  assigneeId: string;
  dueOn: string;
  dueAt: string;
  criteria: string;
  slotStart: string;
  slotMinutes: SlotMinutes;
  slotApprox: string;
}

export const EMPTY_TASK_FORM: TaskForm = {
  title: '', details: '', priority: 'normale', assigneeId: '', dueOn: '', dueAt: '', criteria: '',
  slotStart: '', slotMinutes: '60', slotApprox: '',
};

/** La saisie telle qu'une tâche enregistrée la remplit. */
export function formOf(task: Task): TaskForm {
  return {
    title: task.title,
    details: task.details ?? '',
    priority: task.priority,
    assigneeId: task.assignee_id,
    dueOn: task.due_on ?? '',
    dueAt: task.due_at ? localDateTime(new Date(task.due_at)) : '',
    criteria: task.done_criteria ?? '',
    slotStart: task.planned_start ? localDateTime(new Date(task.planned_start)) : '',
    slotMinutes: slotMinutesOf(task),
    slotApprox: task.time_approx_minutes ? String(task.time_approx_minutes) : '',
  };
}

function approxOf(value: string): TimeApproxMinutes | null {
  const minutes = Number(value);
  return minutes === 30 || minutes === 60 || minutes === 120 ? minutes : null;
}

/** Corps de `POST /tasks` : un objectif porte son heure limite, une tâche sa date. */
export function createBody(form: TaskForm, meId: string | undefined, idempotencyKey: string): Record<string, unknown> {
  return {
    title: form.title.trim(),
    ...(form.details.trim() ? { details: form.details.trim() } : {}),
    priority: form.priority,
    ...(form.dueAt
      ? { dueAt: new Date(form.dueAt).toISOString(), ...(form.criteria.trim() ? { doneCriteria: form.criteria.trim() } : {}) }
      : form.dueOn ? { dueOn: form.dueOn } : {}),
    ...(form.assigneeId && form.assigneeId !== meId ? { assigneeId: form.assigneeId } : {}),
    ...(form.slotStart ? slotBody(form.slotStart, Number(form.slotMinutes), approxOf(form.slotApprox)) : {}),
    idempotencyKey,
  };
}

/**
 * Créneau envoyé par une modification : rien quand il n'a pas bougé (la durée
 * proposée n'est qu'un arrondi : un créneau de 1 h 30 posé au planning ne doit
 * pas devenir 1 h parce qu'on a corrigé le titre) ; la durée enregistrée,
 * exacte, quand seuls le début ou la marge changent.
 */
function changedSlot(form: TaskForm, task: Task, initial: TaskForm): ReturnType<typeof slotBody> | Record<string, never> {
  const moved = form.slotStart !== initial.slotStart || form.slotMinutes !== initial.slotMinutes
    || form.slotApprox !== initial.slotApprox;
  if (!moved) return {};
  const stored = task.planned_start && task.planned_end
    ? Math.round((Date.parse(task.planned_end) - Date.parse(task.planned_start)) / 60_000) : 0;
  const minutes = form.slotMinutes === initial.slotMinutes && stored > 0 ? stored : Number(form.slotMinutes);
  return slotBody(form.slotStart, minutes, approxOf(form.slotApprox));
}

/**
 * Corps de `PATCH /tasks/:id` : celui qui a donné la tâche change tout ; celui
 * qui l'a reçue la planifie seulement (date, créneau). L'heure limite d'un
 * objectif reste à celui qui l'a donné. Le créneau ne part que s'il a changé.
 */
export function patchBody(form: TaskForm, task: Task, author: boolean, initial: TaskForm = formOf(task)): Record<string, unknown> {
  const slot = changedSlot(form, task, initial);
  if (!author) return { ...(task.due_at ? {} : { dueOn: form.dueOn || null }), ...slot };
  return {
    title: form.title.trim(),
    details: form.details.trim(),
    priority: form.priority,
    ...(form.assigneeId && form.assigneeId !== task.assignee_id ? { assigneeId: form.assigneeId } : {}),
    ...(form.dueAt
      ? { dueAt: new Date(form.dueAt).toISOString(), doneCriteria: form.criteria.trim() || null }
      : { dueAt: null, dueOn: form.dueOn || null }),
    ...slot,
  };
}

/** Ce qui manque pour enregistrer, ou `null`. */
export function formProblem(form: TaskForm): string | null {
  if (!form.title.trim()) return t('taches.form.manqueTitre');
  if (form.dueAt && Number.isNaN(new Date(form.dueAt).getTime())) return t('taches.form.heureInvalide');
  return null;
}

/** Les champs changés depuis la tâche enregistrée. */
export function changedFields(initial: TaskForm, form: TaskForm): (keyof TaskForm)[] {
  return (Object.keys(initial) as (keyof TaskForm)[]).filter((key) => initial[key] !== form[key]);
}

export function isTaskForm(value: unknown): value is TaskForm {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Record<string, unknown>;
  return (Object.keys(EMPTY_TASK_FORM) as (keyof TaskForm)[]).every((key) => typeof draft[key] === 'string')
    && ['basse', 'normale', 'haute', 'urgente'].includes(draft.priority as string)
    && (SLOT_MINUTES as readonly string[]).includes(draft.slotMinutes as string);
}
